import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useQuery } from '@/utils/api';

export interface AgoraCapability {
  name: string;
  title: string;
  target: string;
  description: string;
  version: string;
  executor: string;
  surface: string;
  integration: boolean;
  parameters?: unknown[];
  async?: boolean;
}

export interface CapabilitiesData {
  integrations: AgoraCapability[];
  chariotCapabilities: AgoraCapability[];
  allCapabilities: AgoraCapability[];
}

/**
 * Hook to fetch and categorize capabilities from the backend
 * Distinguishes between integrations and Chariot native capabilities
 */
export const useGetCapabilities = () => {
  const axios = useAxios();

  const { data: capabilities, status } = useQuery({
    queryKey: ['capabilities'],
    queryFn: async (): Promise<AgoraCapability[]> => {
      // Fetch both regular capabilities and integrations
      const [regularResponse, integrationResponse] = await Promise.all([
        axios.get('/capabilities'),
        axios.get('/capabilities?integration=true'),
      ]);

      // Combine both arrays
      const regularCapabilities = regularResponse.data || [];
      const integrationCapabilities = integrationResponse.data || [];

      return [...regularCapabilities, ...integrationCapabilities];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    defaultErrorMessage: 'Failed to fetch capabilities',
  });

  const categorizedData = useMemo((): CapabilitiesData => {
    if (!capabilities || !Array.isArray(capabilities)) {
      return {
        integrations: [],
        chariotCapabilities: [],
        allCapabilities: [],
      };
    }

    const integrations = capabilities.filter(
      (cap: AgoraCapability) => cap.integration === true
    );
    const chariotCapabilities = capabilities.filter(
      (cap: AgoraCapability) => cap.integration === false
    );

    return {
      integrations,
      chariotCapabilities,
      allCapabilities: capabilities,
    };
  }, [capabilities]);

  return {
    data: categorizedData,
    status,
    capabilities: categorizedData.allCapabilities,
    integrations: categorizedData.integrations,
    chariotCapabilities: categorizedData.chariotCapabilities,
  };
};
